import { useEffect, useMemo, useState } from 'react'

import { useForm, zodResolver } from '@mantine/form'
import { useFocusWithin } from '@mantine/hooks'
import { z, type ZodSchema } from 'zod'

import { useGetWalletBalance } from '@repo/common/queries/blockchain/useGetWalletBalance'
import {
  type PositionUpdates,
  useGetPoolConfig,
  useGetPositionAmounts,
  useGetPositionManagerPaused,
} from '@repo/common/queries/fission_dex'
import { useActiveFundInfo, useGetFundPrices } from '@repo/common/queries/funds'
import { next_price_tick } from '@repo/common/queries/helpers/tick_helpers'
import { useIsSuccess } from '@repo/common/queries/helpers/utilities'
import { useGetSymbolToDecimal } from '@repo/common/queries/portfolio'
import type { FundSymbol } from '@repo/common/queries/products'
import {
  fundToVaultSymbol,
  useGetTokenAddress,
} from '@repo/common/queries/products'
import { useGetVaultAutoRange } from '@repo/common/queries/vaults'

const default_reason = 'Enter an amount'

type form_values = {
  deposit_fund_tokens: number
  deposit_usdc_tokens: number
  price_low: number
  price_high: number
  active_side: 'USDC' | 'fund'
}
// eslint-disable-next-line max-lines-per-function, complexity
export const useManualFields = ({
  fund_symbol,
}: {
  fund_symbol: FundSymbol
}) => {
  const vault_symbol = fundToVaultSymbol(fund_symbol)
  const [has_initialized, set_initialized] = useState(false)
  const form = useForm<form_values>({
    mode: 'controlled',
    initialValues: {
      deposit_fund_tokens: 10,
      deposit_usdc_tokens: 10,
      price_low: 0,
      price_high: 0,
      active_side: 'USDC',
    },
    validateInputOnChange: true,
    clearInputErrorOnChange: false,
    validate: (values) => {
      return zodResolver(get_schema(values))(values)
    },
  })
  const { get_isSuccess, combine_isSuccess } = useIsSuccess()

  const { balance: balance_fund, isConnected: wallet_isConnected } =
    combine_isSuccess(useGetWalletBalance(fund_symbol))
  const { balance: balance_usdc } = combine_isSuccess(
    useGetWalletBalance('USDC'),
  )
  const { data: fund_prices } = combine_isSuccess(
    useGetFundPrices({ symbol: fund_symbol }),
  )
  const { data: vault_range } = combine_isSuccess(
    useGetVaultAutoRange({ symbol: vault_symbol }),
  )

  const paused_query = combine_isSuccess(useGetPositionManagerPaused())

  const symbol_to_decimal = combine_isSuccess(useGetSymbolToDecimal())
  const { data: fund } = useActiveFundInfo({ symbol: fund_symbol })
  const fund_address = fund?.token_address
  const usdc_address = useGetTokenAddress({ symbol: 'USDC' })
  const { data: pool } = combine_isSuccess(useGetPoolConfig(fund_symbol))

  const updates: PositionUpdates = combine_isSuccess(
    useGetPositionAmounts({ fund_symbol, ...form.getValues() }),
  )

  // eslint-disable-next-line complexity
  const price_limits: {
    price_max: number
    price_first_lower_outside: number
    price_first_upper_outside: number
    // eslint-disable-next-line complexity
  } = useMemo(() => {
    let price_max: number = 10_000
    let price_first_lower_outside = 100
    let price_first_upper_outside = 100

    if (
      fund_address != null &&
      usdc_address != null &&
      symbol_to_decimal.isSuccess &&
      pool != null &&
      vault_range != null
    ) {
      price_max = 10_000_000 // 10 million
      price_first_lower_outside = next_price_tick({
        price_tick: vault_range.tick_low,
        direction: 'down',
        fund_symbol,
        fund_address,
        usdc_address,
        symbol_to_decimal,
        tickSpacing: pool.tickSpacing,
      }).new_price
      price_first_upper_outside = next_price_tick({
        price_tick: vault_range.tick_high,
        direction: 'up',
        fund_symbol,
        fund_address,
        usdc_address,
        symbol_to_decimal,
        tickSpacing: pool.tickSpacing,
      }).new_price

      if (has_initialized == false) {
        form.setValues({
          price_low: price_first_upper_outside,
          price_high: price_first_upper_outside + 10,
        })
        set_initialized(true)
      }
    }
    return {
      price_max,
      price_first_lower_outside,
      price_first_upper_outside,
    }
    // because form is not stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fund_address,
    fund_symbol,
    has_initialized,
    pool,
    symbol_to_decimal,
    usdc_address,
    vault_range,
  ])

  const fund_price = fund_prices?.dex

  const get_schema = (
    values: form_values,
  ): ZodSchema<Omit<form_values, 'active_side'>> => {
    const below_range =
      values.price_low <= price_limits.price_first_lower_outside

    return z.object({
      price_low: z.number({ message: 'Enter low price' }).pipe(
        z.union([
          z.number().max(price_limits.price_first_lower_outside, {
            message: 'Low price must be outside vault range',
          }),
          z.number().min(price_limits.price_first_upper_outside),
        ]),
      ),
      price_high: z
        .number({ message: 'Enter high price' })
        .gt(values.price_low, {
          message: 'High price is below low price',
        })
        .lte(
          below_range
            ? price_limits.price_first_lower_outside
            : price_limits.price_max,
          {
            message: below_range
              ? 'High price must be outside vault range'
              : 'High Price must be below max limit',
          },
        ),
      deposit_usdc_tokens: updates.disabled_usdc
        ? z.number({ message: 'Enter an USDC amount' })
        : z
            .number({ message: 'Enter an USDC amount' })
            .positive({ message: 'Enter an USDC amount' })
            .max(balance_usdc, {
              message: `Not enough USDC balance`,
            }),
      deposit_fund_tokens: updates.disabled_fund
        ? z.number({ message: `Enter an ${fund_symbol} amount` })
        : z
            .number({ message: `Enter an ${fund_symbol} amount` })
            .positive({ message: `Enter an ${fund_symbol} amount` })
            .max(balance_fund, {
              message: `Not enough ${fund_symbol} balance`,
            }),
    })
  }

  const price_low_focus = useFocusWithin()
  const price_high_focus = useFocusWithin()
  const deposit_fund_focus = useFocusWithin()
  const deposit_usdc_focus = useFocusWithin()

  form.watch('deposit_fund_tokens', () => {
    if (!deposit_fund_focus.focused) return

    if (form.getValues().active_side != 'fund') {
      form.setValues({ active_side: 'fund' })
    }
  })

  form.watch('deposit_usdc_tokens', () => {
    if (!deposit_usdc_focus.focused) return

    if (form.getValues().active_side != 'USDC') {
      form.setValues({ active_side: 'USDC' })
    }
  })

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (updates.isSuccess == false) return
    const values: Parameters<typeof form.setValues>[0] = {}
    if (
      updates.price_low != form.getValues().price_low &&
      !price_low_focus.focused
    ) {
      values.price_low = updates.price_low
    }
    if (
      updates.price_high != form.getValues().price_high &&
      !price_high_focus.focused
    ) {
      values.price_high = updates.price_high
    }
    const both_zero = !(
      form.getValues().deposit_fund_tokens > 0 ||
      form.getValues().deposit_usdc_tokens > 0
    )
    if (
      !both_zero &&
      updates.deposit_usdc_tokens != form.getValues().deposit_usdc_tokens &&
      (form.getValues().active_side != 'USDC' ||
        updates.deposit_usdc_tokens == 0) &&
      !deposit_usdc_focus.focused
    ) {
      values.deposit_usdc_tokens = updates.deposit_usdc_tokens
    }
    if (
      !both_zero &&
      updates.deposit_fund_tokens != form.getValues().deposit_fund_tokens &&
      (form.getValues().active_side != 'fund' ||
        updates.deposit_fund_tokens == 0) &&
      !deposit_fund_focus.focused
    ) {
      values.deposit_fund_tokens = updates.deposit_fund_tokens
    }

    if (Object.keys(values).length > 0) {
      form.setValues(values)
    }
  }, [
    deposit_fund_focus.focused,
    deposit_usdc_focus.focused,
    form,
    price_high_focus.focused,
    price_low_focus.focused,
    updates.deposit_fund_tokens,
    updates.deposit_usdc_tokens,
    updates.isSuccess,
    updates.price_high,
    updates.price_low,
  ])

  const is_loading = get_isSuccess() == false || has_initialized == false

  const form_valid = form.isValid() && !is_loading

  const error_reason =
    (paused_query.data == true ? 'Contract paused' : '') ||
    get_schema(form.getValues()).safeParse(form.getValues()).error?.errors[0]
      ?.message ||
    default_reason

  return {
    form,
    vault_symbol,
    wallet_isConnected,
    form_valid,
    paused: paused_query.data ?? false,
    error_reason,
    price_low_focus,
    price_high_focus,
    deposit_fund_focus,
    deposit_usdc_focus,
    disabled_usdc: updates.disabled_usdc,
    disabled_fund: updates.disabled_fund,
    balance_fund,
    price_limits,
    balance_usdc,
    fund_price,
    is_loading,
  }
}
